import { css } from '@emotion/core';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { Icon, Tooltip } from '@/components';
import { FC } from '@/interfaces';
import style from '@/style';
/** 文件翻译的属性接口 */
interface TranslationProgressProps {
  sourceCount: number;
  translatedSourceCount: number;
  checkedSourceCount: number;
  /** 嵌字（已导出）进度百分比（0-100），独立绿色段；不传则不显示 */
  typesetPercent?: number;
  /** 隐藏校对/审核部分（仅翻嵌项目） */
  hideChecked?: boolean;
  type?: 'all' | 'text' | 'line';
  className?: string;
}
/**
 * 文件翻译
 */
export const TranslationProgress: FC<TranslationProgressProps> = ({
  sourceCount,
  translatedSourceCount,
  checkedSourceCount,
  typesetPercent,
  hideChecked = false,
  type = 'all',
  className,
}) => {
  const { formatMessage } = useIntl();
  let translatedSourcePercent = 0;
  let checkedSourcePercent = 0;
  if (sourceCount > 0) {
    if (translatedSourceCount > 0) {
      translatedSourcePercent = (translatedSourceCount / sourceCount) * 100;
    }
    if (checkedSourceCount > 0) {
      checkedSourcePercent = (checkedSourceCount / sourceCount) * 100;
    }
  }

  return (
    <div
      className={classNames('TranslationProgress', className, {
        'TranslationProgress--noSource': sourceCount <= 0,
      })}
      css={css`
        height: ${type === 'line' ? '10px' : '18px'};
        display: flex;
        align-items: center;
        justify-content: flex-start;
        .TranslationProgress__LineBase {
          position: relative;
          width: 100%;
          height: 10px;
          background: var(--moeflow-progressTrack);
          border-radius: ${style.borderRadiusBase};
          overflow: hidden;
        }
        .TranslationProgress__Line {
          position: absolute;
          left: 0;
          top: 0;
          height: 10px;
          border-radius: ${style.borderRadiusBase};
        }
        .TranslationProgress__ProgressTranslatedSourceCount {
          background: var(--moeflow-warningColorLight);
        }
        .TranslationProgress__ProgressCheckedSourceCount {
          background: var(--moeflow-progressStatus);
        }
        .TranslationProgress__ProgressTypeset {
          background: var(--moeflow-progressTypeset);
        }
        .TranslationProgress__Text {
          flex: none;
          font-size: 12px;
        }
        .TranslationProgress__TextItem {
          margin-right: 8px;
          color: ${style.textColorLighter};
          .TranslationProgress__TextItemIcon {
            margin-right: 4px;
            color: ${style.textColorSecondaryLighter};
            font-size: 13px;
          }
        }
        .TranslationProgress__TextItem--unfinished {
          border-radius: 4px;
          color: #fff;
          padding: 0 3px 0 2px;
          font-weight: bold;
          &.TranslationProgress__TextItemTranslated {
            background-color: var(--moeflow-warningColorLight);
          }
          &.TranslationProgress__TextItemChecked {
            background-color: var(--moeflow-progressStatus);
          }
          .TranslationProgress__TextItemIcon {
            margin-right: 2px;
            padding: 1px;
            color: #fff;
          }
        }
        &.TranslationProgress--noSource {
          .TranslationProgress__LineBase {
            background: repeating-linear-gradient(
              45deg,
              var(--moeflow-surface2),
              var(--moeflow-surface2) 15px,
              var(--moeflow-progressTrack) 0,
              var(--moeflow-progressTrack) 30px
            );
          }
        }
      `}
    >
      {(type === 'all' || type === 'text') && (
        <div className="TranslationProgress__Text">
          {sourceCount > 0 ? (
            <>
              <span className="TranslationProgress__TextItem">
                <Icon
                  icon="tag"
                  className="TranslationProgress__TextItemIcon"
                />
                {sourceCount}
              </span>
              <Tooltip
                overlay={formatMessage({
                  id: 'imageTranslator.translateUnfinished',
                })}
                disabled={translatedSourceCount >= sourceCount}
              >
                <span
                  className={classNames(
                    'TranslationProgress__TextItem',
                    'TranslationProgress__TextItemTranslated',
                    {
                      'TranslationProgress__TextItem--unfinished':
                        translatedSourceCount < sourceCount,
                    },
                  )}
                >
                  <Icon
                    icon="check"
                    className="TranslationProgress__TextItemIcon"
                  />
                  {translatedSourceCount}
                </span>
              </Tooltip>
              {!hideChecked && (
                <Tooltip
                  overlay={formatMessage({
                    id: 'imageTranslator.checkUnfinished',
                  })}
                  disabled={checkedSourceCount >= sourceCount}
                >
                  <span
                    className={classNames(
                      'TranslationProgress__TextItem',
                      'TranslationProgress__TextItemChecked',
                      {
                        'TranslationProgress__TextItem--unfinished':
                          checkedSourceCount < sourceCount,
                      },
                    )}
                  >
                    <Icon
                      icon="check-double"
                      className="TranslationProgress__TextItemIcon"
                    />
                    {checkedSourceCount}
                  </span>
                </Tooltip>
              )}
            </>
          ) : (
            <span className="TranslationProgress__TextItem">
              <Icon icon="tag" className="TranslationProgress__TextItemIcon" />0
            </span>
          )}
        </div>
      )}
      {(type === 'all' || type === 'line') && (
        <div className="TranslationProgress__LineBase">
          <div
            className="TranslationProgress__Line TranslationProgress__ProgressTranslatedSourceCount"
            style={{ width: `${translatedSourcePercent}%` }}
          ></div>
          {!hideChecked && (
            <div
              className="TranslationProgress__Line TranslationProgress__ProgressCheckedSourceCount"
              style={{ width: `${checkedSourcePercent}%` }}
            ></div>
          )}
          {typesetPercent !== undefined && typesetPercent > 0 && (
            <div
              className="TranslationProgress__Line TranslationProgress__ProgressTypeset"
              style={{ width: `${typesetPercent}%` }}
            ></div>
          )}
        </div>
      )}
    </div>
  );
};
